import { useState, useEffect, FormEvent, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { authApi, ApiError } from '../services/api';
import { AuthBrand } from '../components/AuthBrand';
import { StaffLoginCodeForm } from '../components/StaffLoginCodeForm';
import { TotpCodeForm } from '../components/TotpCodeForm';
import { buildAuthCallbackUrl, buildFederatedQuery } from '../utils/federatedAuth';
import { isStaffEmail, STAFF_EMAIL_SUFFIX } from '../lib/platforms';
import { Eye, EyeOff, Loader2, CheckCircle, Monitor } from 'lucide-react';

type Stage = 'loading' | 'login' | 'code' | 'totp' | 'authorizing' | 'done' | 'error';

export function FederatedLoginPage() {
  const { login, logout, verifyLoginCode, verifyLogin2FA, resendLoginCode } = useAuth();
  const [searchParams] = useSearchParams();

  const clientId = searchParams.get('client_id');
  const state = searchParams.get('state');
  const codeChallenge = searchParams.get('code_challenge');
  const redirectUri = searchParams.get('redirect_uri');

  const [stage, setStage] = useState<Stage>('loading');
  const [appName, setAppName] = useState<string | null>(null);
  const [staffOnly, setStaffOnly] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const federatedParams = buildFederatedQuery({
    client_id: clientId,
    state,
    code_challenge: codeChallenge,
    redirect_uri: redirectUri,
  });

  useEffect(() => {
    logout().catch(() => {
      /* clear any cached Auth Centre session before SSO */
    });
  }, [logout]);

  const loadApplication = useCallback(() => {
    if (!clientId || !state || !codeChallenge || !redirectUri) {
      setErrorMsg('Missing required parameters (client_id, state, code_challenge, or redirect_uri).');
      setStage('error');
      return;
    }

    setErrorMsg(null);
    setStage('loading');

    authApi
      .getApplication(clientId)
      .then((app) => {
        setAppName(app.name);
        setStaffOnly(app.staffOnly);
        setStage('login');
      })
      .catch((err) => {
        if (err instanceof ApiError && err.statusCode === 404) {
          setErrorMsg('Unknown or inactive application.');
        } else if (err instanceof ApiError && err.statusCode >= 500) {
          setErrorMsg('Auth Centre SSO registry is unavailable. Contact support or retry after redeploy.');
        } else {
          setErrorMsg(err instanceof ApiError ? err.message : 'Unknown or inactive application.');
        }
        setStage('error');
      });
  }, [clientId, state, codeChallenge, redirectUri]);

  useEffect(() => {
    loadApplication();
  }, [loadApplication]);

  const authorize = useCallback(async () => {
    if (!clientId || !state || !codeChallenge || !redirectUri || !appName) return;

    setStage('authorizing');
    try {
      const { code, state: returnedState } = await authApi.federatedAuthorize(
        clientId,
        state,
        codeChallenge,
        redirectUri
      );

      const redirectUrl = buildAuthCallbackUrl(redirectUri, code, returnedState);
      setStage('done');
      setTimeout(() => {
        window.location.href = redirectUrl;
      }, 500);
    } catch (err) {
      if (
        err instanceof ApiError &&
        err.statusCode === 403 &&
        err.message.toLowerCase().includes('verification')
      ) {
        setErrorMsg(err.message);
        setStage('login');
        return;
      }
      setErrorMsg(err instanceof ApiError ? err.message : 'Authorization failed');
      setStage('error');
    }
  }, [clientId, state, codeChallenge, redirectUri, appName]);

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    const normalizedEmail = email.trim().toLowerCase();
    if (staffOnly && !isStaffEmail(normalizedEmail)) {
      setErrorMsg(`This application is restricted to ${STAFF_EMAIL_SUFFIX} staff accounts.`);
      return;
    }

    setIsLoading(true);
    try {
      const result = await login(normalizedEmail, password, clientId ?? undefined);
      if (result.requiresLoginCode) {
        setStage('code');
        return;
      }
      if (result.requires2FA) {
        setStage('totp');
        return;
      }
      await authorize();
    } catch (err) {
      setErrorMsg(err instanceof ApiError ? err.message : 'Login failed');
      setStage('login');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (code: string) => {
    setErrorMsg(null);
    setIsLoading(true);
    try {
      const result = await verifyLoginCode(email.trim().toLowerCase(), code);
      if (result.requires2FA) {
        setStage('totp');
        return;
      }
      await authorize();
    } catch (err) {
      setErrorMsg(err instanceof ApiError ? err.message : 'Verification failed');
      setStage('code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyTotp = async (code: string) => {
    setErrorMsg(null);
    setIsLoading(true);
    try {
      await verifyLogin2FA(email.trim().toLowerCase(), code);
      await authorize();
    } catch (err) {
      setErrorMsg(err instanceof ApiError ? err.message : 'Verification failed');
      setStage('totp');
    } finally {
      setIsLoading(false);
    }
  };

  if (stage === 'loading') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  if (stage === 'error' && !clientId) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="card p-8 max-w-md w-full text-center">
          <Monitor className="w-10 h-10 text-error mx-auto mb-4" />
          <h1 className="text-lg font-semibold mb-2">Invalid Request</h1>
          <p className="text-sm text-foreground-secondary">{errorMsg}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <div className="relative w-full max-w-md">
        <AuthBrand />
        <div className="card p-8">
          {stage === 'done' && (
            <div className="text-center">
              <CheckCircle className="w-12 h-12 text-success mx-auto mb-4" />
              <p className="text-foreground-secondary">
                Signed in. Redirecting to {appName || 'your application'}...
              </p>
            </div>
          )}
          {stage === 'authorizing' && (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-accent mx-auto mb-4" />
              <p className="text-foreground-secondary">Authorizing {appName || 'application'}...</p>
            </div>
          )}
          {stage === 'error' && (
            <div className="text-center">
              <p className="text-error mb-4">{errorMsg}</p>
              <button onClick={loadApplication} className="btn-secondary">
                Try again
              </button>
            </div>
          )}
          {stage === 'totp' && (
            <TotpCodeForm
              email={email.trim().toLowerCase()}
              onVerify={handleVerifyTotp}
              onBack={() => {
                setStage('login');
                setErrorMsg(null);
              }}
              error={errorMsg}
              isLoading={isLoading}
            />
          )}
          {stage === 'code' && (
            <StaffLoginCodeForm
              email={email.trim().toLowerCase()}
              onVerify={handleVerifyCode}
              onResend={() => resendLoginCode(email.trim().toLowerCase())}
              onBack={() => {
                setStage('login');
                setErrorMsg(null);
              }}
              error={errorMsg}
              isLoading={isLoading}
            />
          )}
          {stage === 'login' && (
            <>
              <h1 className="text-xl font-semibold text-center mb-2">Sign in to continue</h1>
              <p className="text-sm text-foreground-secondary text-center mb-6">
                <span className="text-foreground font-medium">{appName}</span> uses Tyaus Auth Centre to
                sign you in.
              </p>
              {errorMsg && (
                <div className="mb-4 p-3 bg-error/10 text-error text-sm rounded-lg">{errorMsg}</div>
              )}
              <form onSubmit={handleLogin} className="space-y-4">
                <input
                  type="email"
                  className="input"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="username"
                />
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="input pr-10"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <button type="submit" disabled={isLoading} className="btn-primary w-full">
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sign in'}
                </button>
              </form>
              <div className="mt-4 text-center text-sm">
                <span className="text-foreground-secondary">Don't have an account? </span>
                <Link to={`/signup${federatedParams}`} className="text-accent hover:text-accent-hover">
                  Sign up
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
